import { Component, Inject, Injector } from '@angular/core';
import { FormControl, Validators } from '@angular/forms';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { DialogData } from 'app/core/interfaces/dialog';
import { MaterialDialog } from 'app/modules/shared/components/dialog/dialog.component';
import { EncounterService } from 'app/core/http/services/encounter.service';
import { Encounter } from 'app/core/interfaces/encounter';
import { BaseComponent } from 'app/modules/shared/components/base.component';
import { PaymentService } from 'app/core/http/services/payment.service';

@Component({
  selector: 'page-schedule-edit-status',
  templateUrl: './schedule-edit-status.component.html',
  styleUrls: ['./schedule-edit-status.component.scss'],
})
export class ScheduleOrganizationEditStatusComponent extends BaseComponent {
  public statusControl = new FormControl('', { nonNullable: true, validators: [Validators.required] });
  public scheduleStatus: any[] = [
    {
      id: 'planned',
      text: 'Pendente',
      color: 'red',
      icon: 'error',
    },
    {
      id: 'in-progress',
      text: 'Em atendimento',
      color: 'orange',
      icon: 'pending',
    },
    {
      id: 'finished',
      text: 'Confirmado',
      color: 'grey',
      icon: 'check_circle',
    },
    {
      id: 'no-show',
      text: 'Não Compareceu',
      color: 'grey',
      icon: 'check_circle',
    },
    {
      id: 'cancelled',
      text: 'Cancelado',
      color: 'grey',
      icon: 'check_circle',
    },
  ];

  public encounter!: any;
  public attendanceCode = '';
  public attendanceCodeError = '';

  constructor(
    public dialogRef: MatDialogRef<MaterialDialog>,

    private encounterService: EncounterService,
    private paymentService: PaymentService,
    @Inject(MAT_DIALOG_DATA) public data: DialogData,
    public injector: Injector,
  ) {
    super(injector);
  }

  ngOnInit(): void {
    if (this.data.param.encounter) {
      this.encounter = this.data.param.encounter as Encounter;
      this.statusControl.setValue(this.encounter.status);
    }
  }

  public confirmCancelled(): Promise<boolean> {
    return new Promise((resolve) => {
      let text = '<b>Exame/Consulta:</b> ';
      text += this.encounter.name;
      text += `<br/><b>Data/Hora:</b> ${this.layout.dateTimeFormat(this.encounter?.startDate)}`;

      this.layout
        .openDialog({
          showButtonCancel: true,
          buttonText: 'Confirmar',
          title: 'Deseja mesmo cancelar o atendimento?',
          content: text,
        })
        .subscribe((retorno: boolean) => {
          resolve(retorno ? true : false);
        });
    });
  }

  async saveStatus() {
    const currentUser = this.auth.currentUserValue;
    let status = this.statusControl.value;

    this.loading = true;
    this.attendanceCodeError = '';

    if (status.includes('no-show') || status.includes('cancelled')) {
      const canCancell = await this.confirmCancelled();
      if (!canCancell) {
        this.loading = false;
        return;
      }
    }

    if (this.requiresAttendanceCode(status)) {
      if (!/^[A-Za-z0-9]{6}$/.test(this.attendanceCode.trim())) {
        this.attendanceCodeError = 'Informe o código de atendimento.';
        this.loading = false;
        return;
      }

      this.paymentService.listByEncounter(this.encounter.encounter_id!).subscribe({
        next: (payment) => {
          if (!payment.payment_id) {
            this.attendanceCodeError = 'Pagamento não encontrado para este atendimento.';
            this.loading = false;
            return;
          }

          this.paymentService.verifyAttendanceCode(payment.payment_id, this.attendanceCode).subscribe({
            next: () => {
              this.openSnackBar('Código validado. Atendimento iniciado com sucesso.', 'success');
              this.encounter.attendance_code_verified = true;
              this.updateEncounterStatus(status, currentUser);
              this.loading = false;
            },
            error: () => {
              this.attendanceCodeError = 'Código inválido ou atendimento não autorizado.';
              this.loading = false;
            },
          });
        },
        error: () => {
          this.attendanceCodeError = 'Não foi possível localizar o pagamento.';
          this.loading = false;
        },
      });
      return;
    }

    if (status === 'no-show') {
      status = 'cancelled';
      this.encounter.cancelledReason = 'no-show';
    }

    this.encounter.status = status;
    this.encounter.modifiedBy = {
      type: currentUser.type,
      name: currentUser.user.name,
      id: currentUser.user.id,
    };

    this.encounterService.updateNew(this.encounter.encounter_id!, this.encounter).subscribe({
      next: (encounter: Encounter) => {
        this.messageSuccess = 'Status do atendimento alterado com sucesso';

        this.openSnackBar('Status do atendimento alterado com sucesso', 'success');
        this.showSuccessMessage = true;
        this.showErrorMessage = false;
        this.dialogRef.close(encounter);

        this.loading = false;
      },
      error: (err) => {
        this.messageError = 'Ocorreu uma falha no servidor. Tente novamente mais tarde';
        this.showErrorMessage = true;
        this.showSuccessMessage = false;
        this.loading = false;
        console.log('error', err);
      },
    });
  }

  isOnlinePayment(): boolean {
    return ['PIX', 'CREDIT_CARD'].includes((this.encounter?.paymentMethod || '').toUpperCase());
  }

  requiresAttendanceCode(status: string): boolean {
    return (
      ['in-progress', 'finished', 'cancelled'].includes(status) &&
      this.encounter?.status !== status &&
      !this.encounter?.attendance_code_verified &&
      this.isOnlinePayment()
    );
  }

  private updateEncounterStatus(status: string, currentUser: any): void {
    this.encounter.status = status;
    this.encounter.modifiedBy = {
      type: currentUser.type,
      name: currentUser.user.name,
      id: currentUser.user.id,
    };

    this.encounterService.updateNew(this.encounter.encounter_id!, this.encounter).subscribe({
      next: (encounter: Encounter) => {
        this.openSnackBar('Código validado. Status do atendimento alterado com sucesso.', 'success');
        this.dialogRef.close(encounter);
        this.loading = false;
      },
      error: () => {
        this.messageError = 'O código foi validado, mas não foi possível alterar o status.';
        this.showErrorMessage = true;
        this.loading = false;
      },
    });
  }
}
